// A change the assistant proposes (apps/server/src/assistantProposals.mjs): what the
// panel gets in a reply's `proposals`, how it shows one, and how Approve applies it.
//
// The assistant never writes. Approve sends the proposed values through the write
// route the app's own forms use (PATCH /api/objects/:object/:id), as the person, so
// that route decides whether they may make the change. Before it does, it reads the
// record again: if a proposed field no longer holds what the proposal showed (someone
// changed it meanwhile), nothing is sent and the card says so.
//
// A note on a contact, lead or opportunity (MOTHER-56) is not a field: those records
// have no Notes field. Approve sends it to POST /api/notes, which adds a `note`
// activity linked to the record, as the person; nothing is there to have changed.

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** One field of a proposal: its value now and the value it would get. Lookups carry the records' names. */
export interface ProposalChange {
  field: string;
  label: string;
  from: unknown;
  to: unknown;
  from_name?: string | null;
  to_name?: string | null;
}

export interface Proposal {
  id: string;
  kind: 'note' | 'edit';
  object: string;
  record_id: string;
  summary: string;
  changes: ProposalChange[];
}

/** A proposal in the panel: waiting, being applied, approved, rejected, or failed (still pending, with the reason). */
export interface ProposalView {
  proposal: Proposal;
  state: 'pending' | 'applying' | 'approved' | 'rejected';
  error?: string | undefined;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string';

function changeOf(value: unknown): ProposalChange | null {
  if (!isRecord(value) || !isText(value.field) || value.field === '' || !isText(value.label) || !('to' in value)) return null;
  const change: ProposalChange = { field: value.field, label: value.label, from: value.from ?? null, to: value.to };
  if (value.from_name === null || isText(value.from_name)) change.from_name = value.from_name;
  if (value.to_name === null || isText(value.to_name)) change.to_name = value.to_name;
  return change;
}

/** The proposals in a reply body; anything not shaped as one is left out. */
export function proposalsFrom(value: unknown): Proposal[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): Proposal[] => {
    if (!isRecord(item) || !isText(item.id) || !isText(item.object) || !isText(item.record_id) || !isText(item.summary)) return [];
    if (item.kind !== 'note' && item.kind !== 'edit') return [];
    if (!Array.isArray(item.changes)) return [];
    const changes = item.changes.map(changeOf);
    if (changes.length === 0 || changes.some((c) => c === null)) return [];
    return [{ id: item.id, kind: item.kind, object: item.object, record_id: item.record_id, summary: item.summary, changes: changes as ProposalChange[] }];
  });
}

/** A value as the card shows it. */
export function shown(value: unknown, name?: string | null): string {
  if (name !== undefined && name !== null && name !== '') return name;
  if (value === null || value === undefined || value === '') return '(empty)';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return String(value);
}

/**
 * The rows the card lists. A note shows only the text it adds; an edit shows each
 * field's value now and the value it would get.
 */
export function cardChanges(proposal: Proposal): { field: string; from?: string; to: string }[] {
  if (isLinkedNote(proposal)) {
    return proposal.changes.map((c) => ({ field: `New ${c.label}`, to: isText(c.to) ? c.to : shown(c.to) }));
  }
  if (proposal.kind === 'note') {
    return proposal.changes.map((c) => {
      const before = isText(c.from) ? c.from.trimEnd() : '';
      const after = isText(c.to) ? c.to : shown(c.to);
      const added = before !== '' && after.startsWith(before) ? after.slice(before.length).trim() : after;
      return { field: `Add to ${c.label}`, to: added };
    });
  }
  return proposal.changes.map((c) => ({ field: c.label, from: shown(c.from, c.from_name), to: shown(c.to, c.to_name) }));
}

/** The objects whose notes are linked activities, made by POST /api/notes (apps/server/src/writes.mjs NOTE_LINKS). */
export const LINKED_NOTE_OBJECTS: readonly string[] = ['contacts', 'leads', 'opportunities'];
const NOTES_PATH = '/api/notes';
const isLinkedNote = (proposal: Proposal) => proposal.kind === 'note' && LINKED_NOTE_OBJECTS.includes(proposal.object);

const recordPath = (proposal: Proposal) =>
  `/api/objects/${encodeURIComponent(proposal.object)}/${encodeURIComponent(proposal.record_id)}`;

async function payloadOf(response: Response): Promise<Record<string, unknown>> {
  try {
    const payload: unknown = await response.json();
    return isRecord(payload) ? payload : {};
  } catch {
    return {};
  }
}

/** The route's error, or `fallback` when it gave none. */
async function refusal(response: Response, fallback: string): Promise<Error> {
  const body = await payloadOf(response);
  return new Error(isText(body.error) && body.error !== '' ? body.error : fallback);
}

/** A note on a contact, lead or opportunity: one POST /api/notes with the note's text, as the person. */
async function applyLinkedNote(fetcher: Fetch, proposal: Proposal): Promise<void> {
  const text = proposal.changes[0]?.to;
  if (proposal.changes.length !== 1 || !isText(text)) throw new Error('This proposal has no note text, so nothing was changed.');
  const response = await fetcher(NOTES_PATH, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ object: proposal.object, record_id: proposal.record_id, summary: text }),
  });
  if (response.ok) return;
  throw await refusal(response, `The note was not added (${response.status}).`);
}

/** True when the record still holds what the proposal showed (numbers may come back as text). */
const unchanged = (now: unknown, then: unknown) =>
  (now ?? null) === (then ?? null) || (now !== null && now !== undefined && then !== null && then !== undefined && String(now) === String(then));

/**
 * Applies a proposal through the write route, as the signed-in person. Resolves once the
 * route has said it made the change; rejects with the route's error, or with why nothing
 * was sent.
 */
export async function applyProposal(fetcher: Fetch, proposal: Proposal): Promise<void> {
  if (isLinkedNote(proposal)) return applyLinkedNote(fetcher, proposal);
  const path = recordPath(proposal);
  const current = await fetcher(path);
  const record = await payloadOf(current);
  if (!current.ok) {
    throw new Error(isText(record.error) && record.error !== '' ? record.error : 'The record could not be read, so nothing was changed.');
  }
  const moved = proposal.changes.find((c) => !unchanged(record[c.field], c.from));
  if (moved !== undefined) throw new Error(`${moved.label} has changed since this was proposed, so nothing was changed. Ask again.`);
  const response = await fetcher(path, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(Object.fromEntries(proposal.changes.map((c) => [c.field, c.to]))),
  });
  if (response.ok) return;
  throw await refusal(response, `The change was not made (${response.status}).`);
}
